// Правила формування розкладу (пакет даних №15, ТЗ 4.2.16.3–4.2.16.4, таблиця 4.17): каталог ключів і розв'язання
// ефективних значень для області (факультет / курс). Ключі — системний перелік (на них спирається логіка солвера),
// тому живуть тут, а не в класифікаторі (docs/build/specs/U16-admin.md, розд. 9).
import { DIRECTORY_BY_CODE } from '../directories/registry.js';
import { DEFAULT_SOLVER_RULES } from '../solver/defaults.js';
/** Каталог правил у порядку показу (типові значення — ТЗ, таблиця 4.17). */
export const SCHEDULING_RULE_KEYS = [
    {
        key: 'maxPairsPerDayStudent',
        label: 'Найбільша кількість пар на день для здобувачів',
        description: 'Жорстке обмеження генератора для кожної групи й підгрупи; перевищення — конфлікт C09.',
        type: 'int',
        min: 1,
        max: 8,
        default: DEFAULT_SOLVER_RULES.maxPairsPerDayStudent,
        unit: 'пар',
    },
    {
        key: 'maxConsecutivePairs',
        label: 'Найбільша кількість пар поспіль',
        description: 'Скільки пар поспіль без перерви («вікна») може мати група.',
        type: 'int',
        min: 1,
        max: 8,
        default: DEFAULT_SOLVER_RULES.maxConsecutivePairs,
        unit: 'пар',
    },
    {
        key: 'sameSubjectOncePerDay',
        label: 'Одна дисципліна для академічної групи не частіше одного разу на день',
        description: 'Типово ввімкнено; порушення — попередження C12.',
        type: 'bool',
        default: DEFAULT_SOLVER_RULES.sameSubjectOncePerDay,
    },
    {
        key: 'maxPairsPerDayTeacher',
        label: 'Найбільша кількість пар на день для викладача',
        description: 'Для викладачів, яким не задано власного значення в картці викладача.',
        type: 'int',
        min: 1,
        max: 8,
        default: 4,
        unit: 'пар',
    },
    {
        key: 'maxTransitionsPerDayStudent',
        label: 'Найбільша кількість переходів між локаціями на день для здобувачів',
        description: 'Переміщення між корпусами й клінічними базами протягом дня.',
        type: 'int',
        min: 0,
        max: 8,
        default: 2,
    },
    {
        key: 'maxTransitionsPerDayTeacher',
        label: 'Найбільша кількість переходів між локаціями на день для викладача',
        description: 'Для викладачів, яким не задано власного значення в картці викладача.',
        type: 'int',
        min: 0,
        max: 8,
        default: 2,
    },
    {
        key: 'maxAcademicHoursPerDay',
        label: 'Найбільша кількість академічних годин на день',
        description: 'Перераховується в пари за кількістю академічних годин у парі (6 год при 2 год/пару = 3 пари); якщо задано й пари на день, діє менше з двох обмежень.',
        type: 'int',
        min: 1,
        max: 16,
        default: null,
        unit: 'год',
    },
    {
        key: 'minBreakMinutes',
        label: 'Мінімальна перерва між парами',
        description: 'Інформаційно: використовується в аналізі перерв між парами (D44), генератор не обмежує.',
        type: 'int',
        min: 0,
        max: 180,
        default: null,
        unit: 'хв',
    },
    {
        key: 'lateSlotFromIndex',
        label: 'Номер пари, з якої пара вважається пізньою',
        description: 'М’який штраф «Пізня пара» у профілях ваг. Не задано — значення з налаштувань університету.',
        type: 'int',
        min: 1,
        max: 8,
        default: null,
    },
];
export const RULE_KEY_VALUES = SCHEDULING_RULE_KEYS.map((d) => d.key);
const DEF_BY_KEY = new Map(SCHEDULING_RULE_KEYS.map((d) => [d.key, d]));
/** Опис ключа правила; undefined — ключ невідомий. */
export function ruleKeyDef(key) {
    return DEF_BY_KEY.get(key);
}
export function isRuleKey(key) {
    return DEF_BY_KEY.has(key);
}
const TRUE_WORDS = new Set(['true', '1', 'так', 'yes', 'y', 'on', 'увімкнено']);
const FALSE_WORDS = new Set(['false', '0', 'ні', 'no', 'n', 'off', 'вимкнено']);
/** Розбір і перевірка значення правила за типом ключа (число з рядка, «так/ні» → bool). */
export function parseRuleValue(def, raw) {
    if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
        return { ok: false, message: 'Значення правила не задано' };
    }
    if (def.type === 'bool') {
        if (typeof raw === 'boolean')
            return { ok: true, value: raw };
        const s = String(raw).trim().toLowerCase();
        if (TRUE_WORDS.has(s))
            return { ok: true, value: true };
        if (FALSE_WORDS.has(s))
            return { ok: true, value: false };
        return { ok: false, message: 'Значення має бути «так» або «ні»' };
    }
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(raw) ? Number(raw) : NaN;
    if (!Number.isFinite(n) || !Number.isInteger(n))
        return { ok: false, message: 'Значення має бути цілим числом' };
    if (def.min !== undefined && n < def.min)
        return { ok: false, message: `Значення має бути не менше ${def.min}` };
    if (def.max !== undefined && n > def.max)
        return { ok: false, message: `Значення має бути не більше ${def.max}` };
    return { ok: true, value: n };
}
const COURSE_FIELD = DIRECTORY_BY_CODE.scheduling_rules?.fields.find((f) => f.key === 'course');
/** Найменший курс правила області COURSE — поле course реєстру scheduling_rules (D47: межі в даних, не константа закладу). */
export const RULE_COURSE_MIN = COURSE_FIELD?.min ?? 1;
/** Найбільший курс правила області COURSE — поле course реєстру scheduling_rules. */
export const RULE_COURSE_MAX = COURSE_FIELD?.max ?? RULE_COURSE_MIN;
/** Допустимі курси правила (для вибору у формі й фільтрі «Чинні правила для…»). */
export const RULE_COURSE_VALUES = Array.from({ length: RULE_COURSE_MAX - RULE_COURSE_MIN + 1 }, (_, i) => RULE_COURSE_MIN + i);
/** Повідомлення про курс поза межами (форма, DTO, хук імпорту). */
export const RULE_COURSE_MESSAGE = `Курс має бути від ${RULE_COURSE_MIN} до ${RULE_COURSE_MAX}`;
/** Перевірка області правила: FACULTY — факультет обов'язковий; COURSE — курс обов'язковий (факультет — за бажанням). */
export function ruleScopeProblem(r) {
    switch (r.scope) {
        case 'ALL':
            if (r.facultyId)
                return 'Для області «Увесь університет» факультет не вказується';
            if (r.course !== null && r.course !== undefined)
                return 'Для області «Увесь університет» курс не вказується';
            return null;
        case 'FACULTY':
            if (!r.facultyId)
                return 'Для області «Факультет» вкажіть факультет';
            if (r.course !== null && r.course !== undefined)
                return 'Для області «Факультет» курс не вказується (оберіть область «Курс»)';
            return null;
        case 'COURSE':
            if (r.course === null || r.course === undefined)
                return 'Для області «Курс» вкажіть курс';
            if (!Number.isInteger(r.course) || r.course < RULE_COURSE_MIN || r.course > RULE_COURSE_MAX)
                return RULE_COURSE_MESSAGE;
            return null;
        default:
            return 'Невідома область правила';
    }
}
/** Ключ області для effectiveMany: «facultyId|course» (порожнє — «*»). */
export function ruleScopeKey(scope) {
    return `${scope.facultyId ?? '*'}|${scope.course ?? '*'}`;
}
/**
 * Найспецифічніше чинне значення ключа для області: COURSE з факультетом > COURSE без факультету > FACULTY > ALL.
 * Неактивні рядки й рядки з некоректним значенням ігноруються; за однакового рівня — перший рядок (сервіс передає
 * найновіші першими).
 */
function pick(rows, def, scope) {
    let best = null;
    let bestRank = -1;
    const facultyId = scope.facultyId ?? null;
    const course = scope.course ?? null;
    for (const row of rows) {
        if (row.key !== def.key || !row.isActive)
            continue;
        let rank = -1;
        let source = 'ALL';
        if (row.scope === 'COURSE') {
            if (course === null || row.course !== course)
                continue;
            if (row.facultyId) {
                if (row.facultyId !== facultyId)
                    continue;
                rank = 3;
            }
            else
                rank = 2;
            source = 'COURSE';
        }
        else if (row.scope === 'FACULTY') {
            if (!facultyId || row.facultyId !== facultyId)
                continue;
            rank = 1;
            source = 'FACULTY';
        }
        else if (row.scope === 'ALL') {
            rank = 0;
            source = 'ALL';
        }
        else
            continue;
        if (rank <= bestRank)
            continue;
        const parsed = parseRuleValue(def, row.value);
        if (!parsed.ok)
            continue;
        best = { value: parsed.value, source, row };
        bestRank = rank;
    }
    return best;
}
/** Ефективні правила з джерелом кожного значення (для сторінки «Чинні правила» і API effective). */
export function resolveRulesDetailed(rows, scope, opts) {
    const items = [];
    const values = new Map();
    for (const def of SCHEDULING_RULE_KEYS) {
        const p = pick(rows, def, scope);
        const item = p
            ? { key: def.key, value: p.value, source: p.source, ...(p.row.id ? { ruleId: p.row.id } : {}), ...(p.row.code ? { ruleCode: p.row.code } : {}) }
            : { key: def.key, value: def.key === 'lateSlotFromIndex' ? opts.lateSlotFromIndex : def.default, source: 'DEFAULT' };
        items.push(item);
        values.set(def.key, item.value);
    }
    const int = (k) => {
        const v = values.get(k);
        return typeof v === 'number' ? v : undefined;
    };
    const pairsItem = items.find((i) => i.key === 'maxPairsPerDayStudent');
    let maxPairs = int('maxPairsPerDayStudent') ?? DEFAULT_SOLVER_RULES.maxPairsPerDayStudent;
    const hours = int('maxAcademicHoursPerDay');
    if (hours !== undefined && opts.academicHoursPerPair > 0) {
        const byHours = Math.max(1, Math.floor(hours / opts.academicHoursPerPair));
        if (byHours < maxPairs) {
            maxPairs = byHours;
            pairsItem.value = byHours;
            pairsItem.limitedBy = 'maxAcademicHoursPerDay';
        }
    }
    const sameSubject = values.get('sameSubjectOncePerDay');
    const rules = {
        maxPairsPerDayStudent: maxPairs,
        maxConsecutivePairs: int('maxConsecutivePairs') ?? DEFAULT_SOLVER_RULES.maxConsecutivePairs,
        lateSlotFromIndex: int('lateSlotFromIndex') ?? opts.lateSlotFromIndex,
        sameSubjectOncePerDay: typeof sameSubject === 'boolean' ? sameSubject : DEFAULT_SOLVER_RULES.sameSubjectOncePerDay,
    };
    const teacherPairs = int('maxPairsPerDayTeacher');
    if (teacherPairs !== undefined)
        rules.maxPairsPerDayTeacher = teacherPairs;
    const studentTransitions = int('maxTransitionsPerDayStudent');
    if (studentTransitions !== undefined)
        rules.maxTransitionsPerDayStudent = studentTransitions;
    const teacherTransitions = int('maxTransitionsPerDayTeacher');
    if (teacherTransitions !== undefined)
        rules.maxTransitionsPerDayTeacher = teacherTransitions;
    return { rules, items };
}
/**
 * Ефективні правила для області запуску/перевірки (контракт U16 → U08, U10, U11):
 * COURSE (+факультет) > FACULTY > ALL > значення за замовчуванням (SCHEDULING_RULE_KEYS, University.lateSlotFromIndex);
 * maxAcademicHoursPerDay → maxPairsPerDayStudent = floor(год / academicHoursPerPair) (менше з двох обмежень).
 */
export function resolveRules(rows, scope, opts) {
    return resolveRulesDetailed(rows, scope, opts).rules;
}
/** Підписи джерела значення (сторінка «Чинні правила»). */
export const RULE_SOURCE_LABELS = {
    COURSE: 'Курс',
    FACULTY: 'Факультет',
    ALL: 'Університет',
    DEFAULT: 'Типове значення',
};
//# sourceMappingURL=rules.js.map